import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Leaf, FileText, Download, CheckCircle, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export default function CharteRSEPage() {
  const { user } = useAuth();
  const administration = ["admin", "bureau"].includes(user?.role);
  const [charte, setCharte] = useState(null);
  const [etat, setEtat] = useState(null);
  const [suivi, setSuivi] = useState(null);
  const [choisis, setChoisis] = useState([]);
  const [accepte, setAccepte] = useState(false);
  const [commentaire, setCommentaire] = useState("");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      const c = await axios.get(`${API}/charte-rse`);
      setCharte(c.data);
    } catch {
      return toast.error("Charte indisponible");
    }
    try {
      const m = await axios.get(`${API}/charte-rse/ma-signature`);
      setEtat(m.data);
    } catch { setEtat(null); }
    if (["admin", "bureau"].includes(user?.role)) {
      try {
        const s = await axios.get(`${API}/charte-rse/signatures`);
        setSuivi(s.data);
      } catch { setSuivi(null); }
    }
  };

  const basculer = (code) =>
    setChoisis((l) =>
      l.includes(code) ? l.filter((c) => c !== code) : [...l, code]);

  const signer = async () => {
    const mini = charte?.minimum_progres || 3;
    if (!accepte)
      return toast.error("Les engagements fermes doivent être acceptés.");
    if (choisis.length < mini)
      return toast.error(`Choisissez au moins ${mini} engagements de progrès.`);
    setEnvoi(true);
    try {
      await axios.post(`${API}/charte-rse/signer`, {
        engagements_progres: choisis,
        accepte_engagements_fermes: true,
        commentaire,
      });
      toast.success("Charte signée");
      setAccepte(false); setChoisis([]); setCommentaire("");
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signature impossible");
    } finally {
      setEnvoi(false);
    }
  };

  const telecharger = async () => {
    try {
      const r = await axios.get(`${API}/charte-rse/pdf/ma-signature`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "charte-rse-signee.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  if (!charte)
    return <div className="p-6 text-muted-foreground">Chargement…</div>;

  const signee = etat?.signature && etat?.a_jour;
  const ancienne = etat?.signature && !etat?.a_jour;
  const nbFermes = charte.sections.reduce((n, s) => n + s.fermes.length, 0);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-4xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Leaf className="h-6 w-6 text-green-600" /> {charte.titre}
          </h1>
          <p className="text-sm text-muted-foreground">
            Version {charte.version} — {charte.date_version}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href={`${API}/charte-rse/pdf`} target="_blank" rel="noreferrer">
              <FileText className="h-4 w-4 mr-1" /> Lire en PDF
            </a>
          </Button>
          {etat?.signature && (
            <Button variant="outline" onClick={telecharger}>
              <Download className="h-4 w-4 mr-1" /> Ma charte signée
            </Button>
          )}
        </div>
      </div>

      {signee && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-5 text-sm">
            <p className="font-medium text-green-900 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Charte signée
            </p>
            <p className="text-green-800 mt-1">
              Par {etat.signature.nom_signataire} le{" "}
              {new Date(etat.signature.signe_le).toLocaleDateString("fr-FR")},
              au nom de {etat.signature.nom_food_truck || "votre food truck"}.
            </p>
          </CardContent>
        </Card>
      )}

      {ancienne && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-5 text-sm text-amber-900">
            <p className="font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Nouvelle version
            </p>
            <p className="mt-1">
              Vous avez signé la version {etat.signature.version}. Le texte a
              changé : merci de le relire et de signer la {charte.version}.
            </p>
          </CardContent>
        </Card>
      )}

      {administration && suivi && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Suivi du réseau — {suivi.nb_a_jour} / {suivi.nb_food_trucks} à jour
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-4">
            <div>
              <p className="font-medium mb-2">
                Signataires ({suivi.signataires.length})
              </p>
              {suivi.signataires.map((s) => (
                <div key={s.id}
                     className="flex items-center justify-between gap-2 border-b py-1">
                  <span>{s.nom_food_truck || s.societe || s.id}</span>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    {new Date(s.signature.signe_le).toLocaleDateString("fr-FR")}
                    {s.a_jour
                      ? <Badge variant="outline">v{s.signature.version}</Badge>
                      : <Badge variant="destructive">
                          v{s.signature.version} à renouveler
                        </Badge>}
                  </span>
                </div>
              ))}
              {!suivi.signataires.length && (
                <p className="text-muted-foreground">
                  Personne n'a encore signé.
                </p>
              )}
            </div>
            <div>
              <p className="font-medium mb-1">
                Pas encore signé ({suivi.a_relancer.length})
              </p>
              <p className="text-muted-foreground">
                {suivi.a_relancer.length
                  ? suivi.a_relancer
                      .map((s) => s.nom_food_truck || s.societe || s.id)
                      .join(", ")
                  : "Tout le réseau a signé."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-5 space-y-2 text-sm text-muted-foreground">
          {charte.introduction.map((p, i) => <p key={`i${i}`}>{p}</p>)}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-5 space-y-2 text-sm">
          {charte.preambule.map((p, i) => <p key={`p${i}`}>{p}</p>)}
        </CardContent>
      </Card>

      {charte.sections.map((s) => (
        <Card key={s.code}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{s.titre}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-3">
            <div>
              <p className="font-medium mb-1">Engagements fermes</p>
              <ul className="list-disc pl-5 space-y-1">
                {s.fermes.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
            {!!s.progres.length && (
              <div>
                <p className="font-medium mb-1">
                  Engagements de progrès
                  {!signee && (
                    <span className="font-normal text-muted-foreground">
                      {" "}— cochez ceux que vous retenez
                    </span>
                  )}
                </p>
                <div className="space-y-1">
                  {s.progres.map((p) => {
                    const retenu = signee
                      ? (etat.signature.engagements_progres || [])
                          .includes(p.code)
                      : choisis.includes(p.code);
                    return (
                      <label key={p.code} className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1"
                               checked={retenu} disabled={signee}
                               onChange={() => basculer(p.code)} />
                        <span className={retenu ? "" : "text-muted-foreground"}>
                          {p.texte}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Ce que le réseau s'engage à faire en retour
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <ul className="list-disc pl-5 space-y-1">
            {charte.engagements_reseau.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Signature et suivi</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-muted-foreground">
          {charte.cadre_signature.map((p, i) => <p key={i}>{p}</p>)}
        </CardContent>
      </Card>

      {!signee && (
        <Card className="border-green-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Signer la charte</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <label className="flex gap-2 items-start">
              <input type="checkbox" className="mt-1" checked={accepte}
                     onChange={(e) => setAccepte(e.target.checked)} />
              <span>
                Je m'engage, au nom de mon food truck, à respecter les{" "}
                {nbFermes} engagements fermes de la version {charte.version}{" "}
                de cette charte.
              </span>
            </label>
            <div>
              <p className="mb-1">
                Engagements de progrès retenus : {choisis.length} /{" "}
                {charte.minimum_progres} minimum
              </p>
              <textarea className="w-full border rounded-md p-2 text-sm"
                        rows={3}
                        placeholder="Précisions, échéances que vous vous donnez, difficultés prévues (facultatif)"
                        value={commentaire}
                        onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            <Button onClick={signer}
                    disabled={envoi || !accepte ||
                              choisis.length < charte.minimum_progres}>
              {envoi ? "Enregistrement…" : "Signer la charte"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
